import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

// A rail is one continuous control: segments share a border and are separated
// by hairlines, so five counters read as a single instrument instead of five
// competing pills. Tone lives only in the count chip — the segment chrome
// stays quiet.
export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'destructive';

// Solid chip for a non-zero count; a zero renders as a bare muted figure so
// live work pops and empty statuses recede.
const countChipClasses: Record<StatusTone, string> = {
  neutral: 'bg-muted text-foreground',
  info: 'bg-info text-info-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  destructive: 'bg-destructive text-destructive-foreground',
};

interface StatusRailProps {
  /** Optional caption segment rendered at the head of the rail. */
  label?: string;
  children: React.ReactNode;
  className?: string;
}

export const StatusRail: React.FC<StatusRailProps> = ({ label, children, className }) => (
  <div
    className={cn(
      'inline-flex h-8 items-stretch overflow-hidden rounded-lg border border-border/70',
      'bg-card shadow-2xs divide-x divide-border/70',
      className
    )}
  >
    {label && (
      <span className="flex items-center bg-muted/50 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
        {label}
      </span>
    )}
    {children}
  </div>
);

interface StatusRailItemProps {
  label: string;
  count: number;
  tone?: StatusTone;
  onClick: () => void;
  isLoading?: boolean;
}

export const StatusRailItem: React.FC<StatusRailItemProps> = ({
  label,
  count,
  tone = 'neutral',
  onClick,
  isLoading,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 text-[13px] font-medium',
      'text-muted-foreground transition-colors duration-fast hover:bg-muted/60 hover:text-foreground',
      // The rail clips outside rings, so the focus ring is inset.
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/40'
    )}
  >
    <span>{label}</span>
    {isLoading ? (
      <Skeleton className="h-3.5 w-5 rounded" />
    ) : count > 0 ? (
      <span
        className={cn(
          'inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-md px-1',
          'text-[11px] font-semibold tabular-nums',
          countChipClasses[tone]
        )}
      >
        {count}
      </span>
    ) : (
      <span className="text-[13px] font-semibold tabular-nums text-muted-foreground/60">0</span>
    )}
  </button>
);
